<template>
  <landing-page-order-card
    :selected="selected"
    :is-discounted-game="isDiscountedGame"
    :discounted-percentage="discountedPercentage"
    :background-image="orderConfiguration.backgroundImage"
    :test-id="testId"
  >
    <template v-if="orderConfiguration.isFeatured" #highlight>
      <div class="text-center m-1 uppercase">
        <strong>{{ $t('landingpage/index/top_seller') }}</strong>
        <fa-icon
          class="absolute top-1/2 right-0 -translate-y-1/2 mr-4 size-4"
          :icon="faStar"
        />
      </div>
    </template>

    <template #default>
      <div class="flex flex-col justify-between h-full">
        <div>
          <p class="text-brand mt-3 mb-0 font-bold text-2xl tracking-wide">
            {{ smallTitle }}
          </p>

          <p class="font-bold mb-2 leading-none text-3xl">{{ mainTitle }}</p>

          <p v-if="selectedLocationData" class="mb-2 text-2xl">
            <template v-if="isDiscountedGame">
              <template v-if="orderConfiguration.unitType === 'Slot'">
                <s class="text-rhino">{{ price }}</s>
                <b>{{ discountedStartingPrice }}*</b>
              </template>
              <i18n-t v-else keypath="landingpage/orderwidget/price" tag="span">
                <template #price>
                  <s class="text-rhino">{{ price }}</s>
                  <b>{{ discountedStartingPrice }}*</b>
                </template>

                <template #duration>
                  {{ props.orderConfiguration.duration }}
                </template>
              </i18n-t>
            </template>
            <i18n-t v-else keypath="landingpage/orderwidget/price" tag="span">
              <template #price>
                <span class="font-bold pr-1">{{ price }}</span
                ><span v-if="convertedEstimate" class="text-rhino">
                  {{ convertedEstimate }}*</span
                >
              </template>

              <template #duration>
                {{ props.orderConfiguration.duration }}
              </template>
            </i18n-t>
          </p>

          <landing-page-order-configuration-extension
            v-for="extension in orderConfiguration.extensions"
            :key="extension.extensionKey"
            :extension="extension"
          />
        </div>
        <div>
          <landing-page-location-dropdown
            :locations="orderConfiguration.locations"
            :selected-location="selectedLocationData"
            @location-selected="setSelectedLocation"
          />
        </div>
      </div>
    </template>

    <template #button>
      <common-button
        v-if="selectedLocationOrderUrl && chargebeePlanId"
        :to="parsedOrderUrl"
        size="sm"
        :disabled="!selectedLocationData.available"
        data-track="landing-page-order-now"
        @click="onOrderClick"
      >
        {{ preOrder ? $t('frontend/preorder-now') : $t('frontend/order-now') }}
      </common-button>
      <common-button
        v-else-if="selectedLocationOrderUrl"
        :href="selectedLocationOrderUrl"
        size="sm"
        :disabled="!selectedLocationData.available"
        data-track="landing-page-order-now"
      >
        {{ preOrder ? $t('frontend/preorder-now') : $t('frontend/order-now') }}
      </common-button>
    </template>
  </landing-page-order-card>
</template>

<script setup lang="ts">
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import {
  type StrapiLocalizedProductConfiguration,
  type StrapiOrderConfigurationContent,
  type StrapiPrice,
} from '~/apollo/types/types';
import { parseRouteUrl } from '~/helpers/checkout';

const props = defineProps<{
  orderConfiguration: StrapiOrderConfigurationContent;
  productKey?: string | null;
  productType: string;
  chargebeePlanId?: string | null;
  preOrder?: boolean;
  selected?: boolean;
  isDiscountedGame?: boolean;
  discountedPercentage?: number;
  testId?: string;
}>();

const { $i18n } = useNuxtApp();

const locationStore = useLocationStore();
const checkoutStore = useCheckoutStore();

function onOrderClick() {
  checkoutStore.resetConfigSelections();
}

const selectedLocationSlug = computed<string>(() => {
  if (
    locationStore.selectedLocation.slug != null &&
    props.orderConfiguration.locations.find(
      (l: StrapiLocalizedProductConfiguration) =>
        l.slug === locationStore.selectedLocation.slug,
    )
  ) {
    return locationStore.selectedLocation.slug;
  }

  if (
    locationStore.bestLatencyLocation != null &&
    props.orderConfiguration.locations.find(
      (l: StrapiLocalizedProductConfiguration) =>
        l.slug === locationStore.bestLatencyLocation,
    )
  ) {
    return locationStore.bestLatencyLocation;
  }

  return (
    $i18n.localeProperties.defaultServerLocation ||
    props.orderConfiguration.locations[0].slug
  );
});

const setSelectedLocation = (location: StrapiLocalizedProductConfiguration) => {
  if (location.available) {
    locationStore.setLocation({
      slug: location.slug,
      orderUrl: location.orderUrl,
    });
  }
};

const selectedLocationData = computed<StrapiLocalizedProductConfiguration>(() =>
  props.orderConfiguration.locations.find(
    (l: StrapiLocalizedProductConfiguration) =>
      l.slug === selectedLocationSlug.value,
  ),
);

const selectedLocationOrderUrl = computed<string>(() => {
  if (selectedLocationData.value == null) return null;
  return selectedLocationData.value.orderUrl;
});

// parseRouteUrl keeps query params (nuxt-i18n strips them from raw :to strings).
const parsedOrderUrl = computed(() => {
  if (!selectedLocationOrderUrl.value) return selectedLocationOrderUrl.value;
  return parseRouteUrl(selectedLocationOrderUrl.value);
});

const smallTitle = computed<string>(() => {
  if (props.orderConfiguration.unitType === 'GB') {
    return `∞ ${$i18n.t('general/non_special_texts/slot_plural')}`;
  }

  return `${props.orderConfiguration.duration} ${$i18n.t(
    'general/non_special_texts/days',
  )}`;
});

const mainTitle = computed<string>(() => {
  if (props.orderConfiguration.unitType === 'GB') {
    return `${props.orderConfiguration.units} GB Ram`;
  }

  return `${props.orderConfiguration.units} ${$i18n.t(
    'general/non_special_texts/slot_plural',
  )}`;
});

const firstSelectedLocationPrices = computed<StrapiPrice>(() =>
  selectedLocationData.value.prices != null &&
  selectedLocationData.value.prices.length > 0
    ? selectedLocationData.value.prices[0]
    : null,
);

const { formatStrapiPrice, discountNativeStrapiPrice } =
  useNumberMoneyFormatting();

const price = computed<string>(() => {
  if (firstSelectedLocationPrices.value == null) return '';
  const formatted = firstSelectedLocationPrices.value.formatted;
  if (formatted && formatted.includes(' (~')) {
    return formatted.split(' (~')[0];
  }
  return formatStrapiPrice(firstSelectedLocationPrices.value);
});

const convertedEstimate = computed<string | null>(() => {
  const p = firstSelectedLocationPrices.value;
  if (p == null || !p.formatted?.includes(' (~')) return null;
  const match = p.formatted.match(/\(~(.+)\)$/);
  return match ? `(~${match[1]})` : null;
});

const discountedStartingPrice = computed<string>(() => {
  const p = firstSelectedLocationPrices.value;
  if (p == null) return '';

  const discount = (props.discountedPercentage ?? 0) / 100;

  // Fallback-currency prices carry the native amount only inside `formatted`
  // ("€8.00 (~£6.50)"); p.value/p.currency hold the converted estimate, so we
  // discount the native amount from the display string to stay in-currency.
  if (p.formatted?.includes(' (~')) {
    const native = discountNativeStrapiPrice(p.formatted, discount);
    if (native) return native;
  }

  return formatStrapiPrice(p, discount);
});
</script>
